import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Users, Search, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import UploadModal from "@/components/UploadModal";
import LibraryCreateHero from "@/components/library/LibraryCreateHero";

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMyLibrariesQuery, useMyLibrariesLazyQuery, useMyLibrariesCountsQuery, useMyDataQuery, useCommunityChannelsQuery, MyLibrary } from "@/generated/graphql";
import { useDebounce, useWindowScroll } from "@uidotdev/usehooks";
import { ProjectCard, type ProjectCardItem } from "@/components/ProjectCard";
import { ListFilterChips } from "@/components/lists/ListFilterChips";
import { ListExpiredGroup } from "@/components/lists/ListExpiredGroup";
import { ListExpiredRow } from "@/components/lists/ListExpiredRow";
import CopyLibraryDialog from "@/components/CopyLibraryDialog";
import { useUrlUpload } from "@/hooks/use-url-upload";
import { useToast } from "@/hooks/use-toast";
import { useUploadFile, UploadProgress } from "@/hooks/use-upload-file";
import { useQuotaUpsellDialog } from "@/hooks/use-quota-upsell-dialog";
import { useHasFeature } from "@/lib/useHasFeature";
import { VideoMetadata } from "@/lib/video-metadata";
import { classifyLibraryOrigin } from "@/lib/library/classifyLibraryOrigin";
import { computeLibraryRefreshVariables } from "@/lib/library/computeLibraryRefreshVariables";
import { listUserLibraries } from "@/lib/library/listUserLibraries";
import { resolveFirstVisit } from "@/lib/library/resolveFirstVisit";
import { buildLibraryFilterChips, listFilterVariables, parseListFilter, type ListFilter } from "@/lib/library/listFilters";
import { pickCommunityPreviewAvatars } from "@/lib/library/pickCommunityPreviewAvatars";
import { isPaidSubscription } from "@/lib/quota/isPaidSubscription";
import { detectAudioTrack } from "@/lib/media/detectAudioTrack";

const PAGE_SIZE = 12;

const LibraryListPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  // The server searches every library (title and channel name); filtering the
  // loaded page only found the 12–24 libraries already on screen.
  const searchTerm = useDebounce(searchQuery.trim(), 300);
  // The chip lives in the URL (?filter=) so going back keeps it.
  const filter = parseListFilter(searchParams.get('filter'), 'library');
  const setFilter = (next: ListFilter) => {
    setSearchParams((prev) => {
      if (next === 'all') prev.delete('filter');
      else prev.set('filter', next);
      return prev;
    }, { replace: true });
  };
  const { data, loading, error, fetchMore, refetch } = useMyLibrariesQuery({
    variables: { limit: PAGE_SIZE, offset: 0, search: searchTerm || undefined, ...listFilterVariables(filter) },
    fetchPolicy: 'cache-and-network',
  });
  // The expired group at the end of "Todos" pages on its own, outside the
  // cached list (which holds whatever the chips show).
  const [loadExpiredLibraries] = useMyLibrariesLazyQuery({ fetchPolicy: 'no-cache' });
  const { data: myData, refetch: refetchMyData } = useMyDataQuery();
  // Authoritative totals from the backend. The loaded `myLibraries` page is
  // paginated, so counting it client-side under-reports until every page has
  // scrolled in. These counts cover the user's full library.
  const { data: countsData, refetch: refetchCounts } = useMyLibrariesCountsQuery({
    fetchPolicy: 'cache-and-network',
  });
  const isPaidPlan = useMemo(
    () => isPaidSubscription(myData?.myData?.subscription),
    [myData?.myData?.subscription],
  );
  const hasProFeatures = useHasFeature('pro_features');
  const { dialog: quotaUpsellDialog, openQuotaUpsell } = useQuotaUpsellDialog();
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isFetchingMoreRef = useRef(false);
  const [scroll] = useWindowScroll();
  const scrollY = scroll?.y ?? 0;

  const [duplicateLibrary, setDuplicateLibrary] = useState<MyLibrary | null>(null);

  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { getUploadUrl, error: uploadError, createBlobUrl, revokeBlobUrl, getVideoDuration, abortUpload } = useUploadFile();
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);

  const {
    videoUrl,
    setVideoUrl,
    urlVideoMetadata,
    isLoadingMetadata,
    communityMatch, setCommunityMatch,
    handleUrlUpload, resetUpload, navigateToCommunityMatch,
  } = useUrlUpload({
    maxVideoSeconds: myData?.myData?.subscription?.plan?.max_library_video_seconds,
    hasProAccess: hasProFeatures,
    onProRequired: () => openQuotaUpsell('paid_feature'),
  });

  // Users routed here from the marketing site (directly, or bounced from
  // /projects by the objective redirect) arrive with the pasted link in ?url=.
  // Prefill + open the create modal, then drop the param to avoid re-triggering.
  const urlParamConsumed = useRef(false);
  useEffect(() => {
    if (urlParamConsumed.current) return;
    const incomingUrl = searchParams.get("url");
    if (!incomingUrl) return;
    urlParamConsumed.current = true;
    setVideoUrl(incomingUrl);
    handleUrlUpload(incomingUrl);
    const next = new URLSearchParams(searchParams);
    next.delete("url");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('focus') !== 'video-url-input') return;

    const input = document.getElementById('video-url-input');
    if (input) {
      input.focus();
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    setSearchParams((prev) => {
      prev.delete('focus');
      return prev;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  const libraries = useMemo(() => data?.myLibraries ?? [], [data?.myLibraries]);

  // Tracks how many libraries are currently loaded (incl. paginated pages) so the
  // poll below can refresh the full range without resetting the interval timer.
  const loadedCountRef = useRef(libraries.length);
  loadedCountRef.current = libraries.length;

  const visibleLibraries = useMemo(() => listUserLibraries(libraries), [libraries]);

  // A new search or chip starts from its first page again.
  useEffect(() => {
    setHasMore(true);
  }, [searchTerm, filter]);

  // Prefer the authoritative backend totals, then the loaded list; while both
  // are in flight this is not a first visit (see resolveFirstVisit).
  const counts = countsData?.myLibrariesCounts;
  const isFirstClip = resolveFirstVisit({
    serverTotal: counts ? counts.mine + counts.community : null,
    // A search or a chip that matches nothing is not an empty account.
    loadedCount: data && !searchTerm && filter === 'all' ? libraries.length : null,
  });
  // The channel avatars only appear on the first-visit card; the one-row bar
  // returning users get doesn't show them (186 KB and ~0.7 s in production).
  const { data: communityChannelsData } = useCommunityChannelsQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isFirstClip,
  });
  const communityPreview = useMemo(
    () => pickCommunityPreviewAvatars(communityChannelsData?.communityChannels ?? []),
    [communityChannelsData?.communityChannels],
  );
  const filterChips = buildLibraryFilterChips(counts);
  const expiredCount = counts?.expired ?? 0;
  const showExpiredGroup = filter === 'all' && !loading && !error && !hasMore && expiredCount > 0;

  const handleGenerate = () => {
    const url = videoUrl.trim();
    if (!url) {
      toast({
        title: t('library.urlRequired'),
        description: t('library.urlRequiredDescription'),
      });
      return;
    }
    handleUrlUpload(url);
  };

  const handleUploadFile = () => {
    if (!hasProFeatures) {
      openQuotaUpsell('paid_feature');
      return;
    }
    fileInputRef.current?.click();
  };

  const cleanupBlobUrl = () => {
    if (blobUrl) revokeBlobUrl(blobUrl);
    setBlobUrl(null);
    setSelectedFile(null);
    setUploadProgress(null);
    setUploadedVideoUrl(null);
    setIsUploadComplete(false);
    setIsUploaded(false);
    setVideoDuration(0);
  };

  const startUpload = async (file: File) => {
    try {
      setUploadProgress({ percentage: 0, uploadedBytes: 0, totalBytes: file.size, uploadSpeed: 0, remainingTime: 0 });
      const uploadUrl = await getUploadUrl(file, (progress) => {
        setUploadProgress(progress);
      });
      if (uploadUrl) {
        setUploadedVideoUrl(uploadUrl);
        setIsUploadComplete(true);
        setUploadProgress(null);
        setIsUploaded(true);
      }
    } catch (error) {
      if (!(error instanceof Error && error.message === 'Upload was aborted')) {
        toast({
          title: t('errors.uploadError'),
          description: uploadError || t('errors.uploadErrorOccurred'),
          variant: "destructive",
        });
      }
      setUploadProgress(null);
      setIsUploadComplete(false);
    }
  };

  const handleFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.type !== 'video/mp4') {
      toast({
        title: t('errors.invalidFileType'),
        description: t('errors.onlyMp4Accepted'),
        variant: "destructive",
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    let duration: number | null = null;
    try {
      duration = await getVideoDuration(file);
    } catch (error) {
      console.error('Error getting video duration:', error);
    }
    if (!duration) {
      toast({
        title: t('errors.failedToReadVideo'),
        description: t('errors.couldNotDetermineDuration'),
        variant: "destructive",
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // A video without audio cannot be transcribed, so processing would fail
    if (await detectAudioTrack(file) === 'absent') {
      toast({
        title: t('errors.videoHasNoAudio'),
        description: t('errors.videoHasNoAudioDescription'),
        variant: "destructive",
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploadProgress(null);
    setIsUploadComplete(false);
    setUploadedVideoUrl(null);
    setVideoDuration(duration);
    setBlobUrl(createBlobUrl(file));
    setSelectedFile(file);
    setIsUploadModalOpen(true);
    startUpload(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const fileVideoMetadata: VideoMetadata | null = useMemo(() => {
    if (urlVideoMetadata || !blobUrl || !selectedFile) return null;
    return {
      url: isUploadComplete && uploadedVideoUrl ? uploadedVideoUrl : blobUrl,
      title: selectedFile.name.replace(/\.[^/.]+$/, ''),
      duration: videoDuration,
      platform: 'http',
      thumbnail: undefined,
      best_video_format: {
        format_id: 'upload',
        ext: 'mp4',
        url: blobUrl,
        has_audio: true,
        has_video: true,
      },
    };
  }, [urlVideoMetadata, blobUrl, selectedFile, videoDuration, isUploadComplete, uploadedVideoUrl]);

  const handleModalClose = () => {
    if (urlVideoMetadata) {
      resetUpload();
      return;
    }
    if (uploadProgress && !isUploadComplete) abortUpload();
    setIsUploadModalOpen(false);
    cleanupBlobUrl();
  };

  const handleLoadMore = async () => {
    if (isFetchingMoreRef.current || loading || error || !hasMore) return;
    isFetchingMoreRef.current = true;
    setIsFetchingMore(true);
    try {
      const prevCount = libraries.length;
      const res = await fetchMore({
        variables: { limit: PAGE_SIZE, offset: prevCount },
      });
      const fetched = res.data?.myLibraries?.length ?? 0;
      if (fetched < PAGE_SIZE) setHasMore(false);
    } finally {
      isFetchingMoreRef.current = false;
      setIsFetchingMore(false);
    }
  };

  useEffect(() => {
    if (loading || !hasMore) return;
    const currentY = scrollY ?? 0;
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const docH = document.documentElement.scrollHeight || document.body.scrollHeight;
    const distanceFromBottom = docH - (currentY + viewportH);
    if (distanceFromBottom < 600) {
      handleLoadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollY, hasMore, loading]);

  // A first page shorter than PAGE_SIZE (an empty one included) is the end.
  useEffect(() => {
    if (!loading && data && libraries.length < PAGE_SIZE) {
      setHasMore(false);
    }
  }, [loading, data, libraries.length]);

  // Poll for processing libraries
  const hasProcessingLibraries = useMemo(() => {
    return libraries.some((lib) => !['success', 'failure', 'expired'].includes(lib.curation_status));
  }, [libraries]);

  useEffect(() => {
    if (!hasProcessingLibraries) return;
    const pollInterval = setInterval(() => {
      // Refetch the full loaded range, not just page 1 — refetch()'s default
      // (offset 0, limit PAGE_SIZE) would hit the merge function's offset-0
      // "replace" branch and truncate paginated items out of the list.
      refetch(computeLibraryRefreshVariables({ loadedCount: loadedCountRef.current, pageSize: PAGE_SIZE }));
      // Keep the empty-state totals in sync as libraries finish processing or fail out.
      refetchCounts();
    }, 10000);
    return () => clearInterval(pollInterval);
  }, [hasProcessingLibraries, refetch, refetchCounts]);

  return (
    <div className="relative min-h-[100dvh] bg-background p-4 lg:p-6">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-80"
        style={{ background: 'radial-gradient(ellipse 70% 280px at 50% 0%, hsl(var(--primary) / 0.09) 0%, transparent 100%)' }}
      />
      <div
        data-testid="library-page-stack"
        className={`relative w-full max-w-screen-2xl mx-auto ${isFirstClip ? 'flex min-h-[calc(100dvh-5.5rem)] flex-col justify-center py-4' : 'space-y-5 lg:space-y-6'}`}
      >
        <LibraryCreateHero
          videoUrl={videoUrl}
          isLoadingMetadata={isLoadingMetadata}
          isFirstClip={isFirstClip}
          compact={!isFirstClip}
          communityAvatars={communityPreview.avatars}
          communityChannelCount={communityPreview.totalCount}
          onVideoUrlChange={setVideoUrl}
          onGenerate={handleGenerate}
          onUploadFile={handleUploadFile}
        />

        {!isFirstClip && (
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 id="library-project-history" className="text-lg font-bold sm:text-xl">{t('library.projectHistory')}</h2>
            <div className="relative w-full sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t('library.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 pl-10 pr-8"
              />
              {searchQuery && (
                <button
                  type="button"
                  aria-label={t('lists.clearSearch')}
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <ListFilterChips chips={filterChips} value={filter} onChange={setFilter} />

          {filter === 'expired' ? (
            <div className="rounded-xl border border-border px-3 sm:px-4">
              {loading && <div className="py-6 text-center text-xs text-muted-foreground">{t('library.loadingMore')}</div>}
              {!loading && !error && libraries.map((library) => (
                <ListExpiredRow key={library.id} item={library as unknown as ProjectCardItem} type="library" />
              ))}
              {!loading && !error && libraries.length === 0 && (
                <p className="py-6 text-center text-sm text-muted-foreground">{t('lists.noExpired')}</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
              {loading && Array.from({ length: 8 }).map((_, idx) => (
                <Card key={`skeleton-${idx}`} className="flex animate-pulse gap-3 p-2 sm:flex-col sm:gap-0 sm:p-0">
                  <div className="aspect-video w-32 shrink-0 rounded-lg bg-muted sm:w-full sm:rounded-none" />
                  <div className="flex-1 space-y-2 sm:p-3">
                    <div className="h-3 w-3/4 rounded bg-muted" />
                    <div className="h-3 w-1/2 rounded bg-muted" />
                    <div className="hidden h-9 rounded bg-muted sm:block" />
                  </div>
                </Card>
              ))}

              {!loading && !error && visibleLibraries.map((library) => (
                <ProjectCard
                  key={library.id}
                  project={library as unknown as ProjectCardItem}
                  type="library"
                  origin={classifyLibraryOrigin(library)}
                  onDuplicate={() => setDuplicateLibrary(library as unknown as MyLibrary)}
                />
              ))}

              {!loading && !error && visibleLibraries.length === 0 && (
                <div className="col-span-full py-8 text-center text-sm text-muted-foreground">
                  <p>{t('library.noItemsFound')}</p>
                </div>
              )}
            </div>
          )}
          {error && (
            <div className="text-center text-sm text-destructive">
              {t('library.failedToLoad')}
            </div>
          )}
          {isFetchingMore && (
            <div className="py-2 text-center text-xs text-muted-foreground">{t('library.loadingMore')}</div>
          )}

          {showExpiredGroup && (
            <ListExpiredGroup
              key={searchTerm}
              count={expiredCount}
              labelKey={searchTerm ? 'lists.expiredMatches' : 'library.expiredGroup'}
              pageSize={PAGE_SIZE}
              loadPage={async (offset) => {
                const result = await loadExpiredLibraries({
                  variables: { limit: PAGE_SIZE, offset, search: searchTerm || undefined, expired: true },
                });
                return (result.data?.myLibraries ?? []) as unknown as ProjectCardItem[];
              }}
              renderRow={(item) => <ListExpiredRow key={item.id} item={item} type="library" />}
            />
          )}
        </div>
        )}
      </div>

      {/* Upload Modal */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".mp4,video/mp4"
        className="hidden"
        onChange={handleFileSelection}
      />
      <UploadModal
        isOpen={!!urlVideoMetadata || isUploadModalOpen}
        onClose={handleModalClose}
        onUploadComplete={() => {
          resetUpload();
          setIsUploadModalOpen(false);
          cleanupBlobUrl();
          refetchMyData();
          refetchCounts();
        }}
        videoMetadata={urlVideoMetadata ?? fileVideoMetadata}
        uploadProgress={uploadProgress}
        isUploadComplete={urlVideoMetadata ? true : isUploadComplete}
        isUploaded={urlVideoMetadata ? false : isUploaded}
        mode="library"
      />
      {quotaUpsellDialog}

      <CopyLibraryDialog
        isOpen={!!duplicateLibrary}
        onClose={() => setDuplicateLibrary(null)}
        title={t('projectCard.duplicateProject')}
        isPaidPlan={isPaidPlan}
        library={duplicateLibrary ? {
          name: duplicateLibrary.name,
          // Reconstruct YouTube watch URLs for user uploads so duplicates
          // still submit a provider URL instead of an S3 object.
          base_video_url: duplicateLibrary.youtube_video_id
            ? `https://www.youtube.com/watch?v=${duplicateLibrary.youtube_video_id}`
            : duplicateLibrary.base_video_url,
          duration_seconds: duplicateLibrary.duration_seconds,
          format: duplicateLibrary.format,
          language: duplicateLibrary.language,
          audio_language: duplicateLibrary.audio_language,
          audio_track: duplicateLibrary.audio_track,
          start_time: duplicateLibrary.start_time,
          end_time: duplicateLibrary.end_time,
          source_library_id: duplicateLibrary.source_library_id ?? null,
        } : null}
      />

      {/* Community Library Available Dialog */}
      <Dialog open={!!communityMatch} onOpenChange={(open) => {
        if (!open) {
          setCommunityMatch(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('library.communityAvailableTitle')}</DialogTitle>
            <DialogDescription>
              {t('library.communityAvailableDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button
              variant="gradient"
              onClick={navigateToCommunityMatch}
            >
              <Users className="w-4 h-4 mr-2" />
              {t('library.goToCommunityLibrary')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LibraryListPage;
